import React, { useState } from 'react';
import { Minus, Plus, ChevronUp, ChevronDown, X, Maximize2, Settings } from 'lucide-react';
import { Dropdown } from '../ui/Dropdown';
import { TradeData } from '../../utils/krakenAPI';
interface OrderBookEntry {
  price: number;
  quantity: number;
  total: number;
}
interface OrderBookProps {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  spread?: number;
  spreadPercent?: number;
  trades?: TradeData[];
  height?: string;
  style?: React.CSSProperties;
}
export function OrderBook({
  bids,
  asks,
  spread,
  spreadPercent,
  trades = [],
  height = 'h-full',
  style
}: OrderBookProps) {
  const [grouping, setGrouping] = useState(0.1);
  const [activeView, setActiveView] = useState<'orderbook' | 'trades' | 'summary'>('orderbook');
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [showGroupingMenu, setShowGroupingMenu] = useState(false);
  const groupingOptions = [0.01, 0.1, 1.0, 10.0];
  const viewOptions = [{
    value: 'orderbook',
    label: 'Order book'
  }, {
    value: 'trades',
    label: 'Market trades'
  }, {
    value: 'summary',
    label: 'Market summary'
  }];
  // Group orders by price level based on grouping setting
  const groupOrders = (orders: OrderBookEntry[], groupingSize: number): OrderBookEntry[] => {
    const grouped = new Map<number, {
      quantity: number;
      total: number;
    }>();
    orders.forEach(order => {
      // Round price to nearest grouping level
      const groupedPrice = Math.floor(order.price / groupingSize) * groupingSize;
      if (grouped.has(groupedPrice)) {
        const existing = grouped.get(groupedPrice)!;
        grouped.set(groupedPrice, {
          quantity: existing.quantity + order.quantity,
          total: existing.total + order.total
        });
      } else {
        grouped.set(groupedPrice, {
          quantity: order.quantity,
          total: order.total
        });
      }
    });
    // Convert back to array and sort
    return Array.from(grouped.entries()).map(([price, data]) => ({
      price,
      quantity: data.quantity,
      total: data.total
    })).sort((a, b) => b.price - a.price); // Sort descending for asks, will reverse for bids
  };
  // Apply grouping to bids and asks
  const groupedBids = groupOrders(bids, grouping).sort((a, b) => b.price - a.price);
  const groupedAsks = groupOrders(asks, grouping).sort((a, b) => b.price - a.price);
  // Determine how many rows to show based on grouping (larger grouping = fewer unique levels, so show more of what we have)
  const rowsToShow = grouping >= 10 ? 50 : grouping >= 1 ? 35 : 25;
  // Calculate live spread from grouped data
  const liveSpread = groupedBids.length > 0 && groupedAsks.length > 0 ? groupedAsks[groupedAsks.length - 1].price - groupedBids[0].price : undefined;
  const liveSpreadPercent = liveSpread && groupedBids.length > 0 ? liveSpread / groupedBids[0].price * 100 : undefined;
  const maxTotal = Math.max(...groupedBids.map(b => b.total), ...groupedAsks.map(a => a.total));
  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };
  // Determine decimal places based on grouping
  const getPriceDecimals = (groupingSize: number): number => {
    if (groupingSize >= 10) return 0;
    if (groupingSize >= 1) return 1;
    if (groupingSize >= 0.1) return 1;
    return 2;
  };
  const priceDecimals = getPriceDecimals(grouping);
  const OrderRow = ({
    entry,
    type
  }: {
    entry: OrderBookEntry;
    type: 'bid' | 'ask';
  }) => {
    const depthPercent = entry.total / maxTotal * 100;
    const bgColor = type === 'bid' ? 'bg-success/10' : 'bg-danger/10';
    return <div className="relative group hover:bg-background-elevated transition-colors" data-id="element-1445">
        {/* Depth bar */}
        <div className={`absolute inset-y-0 ${type === 'bid' ? 'right-0' : 'left-0'} ${bgColor} transition-all`} style={{
        width: `${depthPercent}%`
      }} data-id="element-1446" />

        {/* Content */}
        <div className="relative grid grid-cols-3 gap-2 px-3 py-1 text-xs font-mono" data-id="element-1447">
          <div className={`text-right ${type === 'bid' ? 'text-success' : 'text-[#E85D75]'}`} data-id="element-1448">
            {formatNumber(entry.price, priceDecimals)}
          </div>
          <div className="text-right text-text-primary" data-id="element-1449">
            {formatNumber(entry.quantity, 8)}
          </div>
          <div className="text-right text-text-secondary" data-id="element-1450">
            {formatNumber(entry.total)}
          </div>
        </div>
      </div>;
  };
  return <div className={`flex flex-col ${height} bg-background-primary border-b border-border-subtle`} style={style} data-id="element-1451">
      {/* Header with Pills */}
      <div className="relative flex items-center gap-2 px-3 py-2 border-b border-border-subtle" data-id="element-1452">
        {/* View Pills - Scrollable with proper padding to avoid button overlap */}
        <div className="flex items-center gap-2 flex-shrink-0 overflow-x-auto pr-48 scrollbar-hide" data-id="element-1453">
          <button onClick={() => setActiveView('orderbook')} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${activeView === 'orderbook' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-1454">
            Order book
            {activeView === 'orderbook' && <X size={14} className="text-text-secondary" data-id="element-1455" />}
          </button>

          <button onClick={() => setActiveView('trades')} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${activeView === 'trades' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-1456">
            Market trades
            {activeView === 'trades' && <X size={14} className="text-text-secondary" data-id="element-1457" />}
          </button>

          <button onClick={() => setActiveView('summary')} className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${activeView === 'summary' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-1458">
            Market summary
            {activeView === 'summary' && <X size={14} className="text-text-secondary" data-id="element-1459" />}
          </button>
        </div>

        {/* Action Buttons - Fixed Position with wider gradient */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-gradient-to-l from-background-primary via-background-primary/95 to-transparent pl-16 pr-0" data-id="element-1460">
          <div className="relative" data-id="element-1461">
            <button onClick={() => setShowViewMenu(!showViewMenu)} className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1462">
              <Plus size={16} className="text-text-secondary" data-id="element-1463" />
            </button>

            {showViewMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowViewMenu(false)} data-id="element-1464" />
                <div className="absolute right-0 top-full mt-2 bg-background-elevated border border-border-subtle rounded-lg shadow-lg py-1 z-20 min-w-[200px]" data-id="element-1465">
                  <div className="px-3 py-2 text-xs text-text-tertiary uppercase tracking-wide" data-id="element-1466">
                    Suggested
                  </div>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1467">
                    Explore
                  </button>

                  <div className="px-3 py-2 text-xs text-text-tertiary uppercase tracking-wide mt-2" data-id="element-1468">
                    Market Info
                  </div>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1469">
                    Depth chart
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1470">
                    Aggressor ratio
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1471">
                    Trade count
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1472">
                    Order book analytics
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1473">
                    Volatility
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1474">
                    Volume
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1475">
                    Technical analysis
                  </button>

                  <div className="px-3 py-2 text-xs text-text-tertiary uppercase tracking-wide mt-2" data-id="element-1476">
                    Trading Activity
                  </div>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1477">
                    Portfolio
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1478">
                    Activity
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1479">
                    Funding transactions
                  </button>

                  <div className="px-3 py-2 text-xs text-text-tertiary uppercase tracking-wide mt-2" data-id="element-1480">
                    Order Form
                  </div>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1481">
                    Simple order form
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-secondary transition-colors" data-id="element-1482">
                    1-Click trading
                  </button>
                </div>
              </>}
          </div>

          <button className="px-2 py-1 text-xs text-text-secondary hover:text-text-primary bg-background-elevated rounded transition-colors" data-id="element-1483">
            +2
          </button>

          <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1484">
            <Maximize2 size={16} className="text-text-secondary" data-id="element-1485" />
          </button>

          <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" data-id="element-1486">
            <Settings size={16} className="text-text-secondary" data-id="element-1487" />
          </button>
        </div>
      </div>

      {/* View Content */}
      {activeView === 'orderbook' && <>
          {/* Grouping Controls */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle" data-id="element-1488">
            <div className="flex items-center gap-2" data-id="element-1489">
              <button onClick={() => {
            const currentIndex = groupingOptions.indexOf(grouping);
            if (currentIndex > 0) {
              setGrouping(groupingOptions[currentIndex - 1]);
            }
          }} disabled={grouping === groupingOptions[0]} className={`p-1.5 rounded transition-colors ${grouping === groupingOptions[0] ? 'text-text-tertiary cursor-not-allowed opacity-40' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated'}`} data-id="element-1490">
                <Minus size={14} data-id="element-1491" />
              </button>

              <div className="relative" data-id="element-1492">
                <button onClick={() => setShowGroupingMenu(!showGroupingMenu)} className="flex items-center gap-1 px-2 py-1 text-xs font-mono text-text-primary bg-background-elevated hover:bg-background-secondary rounded transition-colors" data-id="element-1493">
                  {grouping >= 1 ? grouping.toFixed(0) : grouping.toFixed(2)}
                  {showGroupingMenu ? <ChevronUp size={12} className="text-text-secondary" data-id="element-1494" /> : <ChevronDown size={12} className="text-text-secondary" data-id="element-1495" />}
                </button>

                {showGroupingMenu && <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowGroupingMenu(false)} data-id="element-1496" />
                    <div className="absolute left-0 top-full mt-1 bg-background-elevated border border-border-subtle rounded-lg shadow-lg py-1 z-20 min-w-[80px]" data-id="element-1497">
                      {groupingOptions.map(option => <button key={option} onClick={() => {
                  setGrouping(option);
                  setShowGroupingMenu(false);
                }} className={`w-full px-3 py-2 text-left text-xs font-mono transition-colors ${grouping === option ? 'bg-background-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-secondary'}`} data-id="element-1498">
                          {option >= 1 ? option.toFixed(0) : option.toFixed(2)}
                        </button>)}
                    </div>
                  </>}
              </div>

              <button onClick={() => {
            const currentIndex = groupingOptions.indexOf(grouping);
            if (currentIndex < groupingOptions.length - 1) {
              setGrouping(groupingOptions[currentIndex + 1]);
            }
          }} disabled={grouping === groupingOptions[groupingOptions.length - 1]} className={`p-1.5 rounded transition-colors ${grouping === groupingOptions[groupingOptions.length - 1] ? 'text-text-tertiary cursor-not-allowed opacity-40' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated'}`} data-id="element-1499">
                <Plus size={14} data-id="element-1500" />
              </button>
            </div>
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[10px] text-text-tertiary uppercase tracking-wider border-b border-border-subtle font-medium" data-id="element-1501">
            <div className="text-right" data-id="element-1502">Price</div>
            <div className="text-right" data-id="element-1503">Quantity</div>
            <div className="text-right" data-id="element-1504">Total</div>
          </div>

          {/* Asks (Sell orders) */}
          <div className="flex-1 overflow-y-auto flex flex-col justify-end" data-id="element-1505">
            <div className="flex flex-col-reverse" data-id="element-1506">
              {groupedAsks.map((ask, idx) => <OrderRow key={`ask-${idx}`} entry={ask} type="ask" data-id="element-1507" />)}
            </div>
          </div>

          {/* Spread */}
          {liveSpread !== undefined && <div className="flex items-center justify-between px-3 py-2 bg-background-elevated border-y border-border-subtle flex-shrink-0" data-id="element-1508">
              <span className="text-xs text-text-secondary" data-id="element-1509">Spread</span>
              <div className="flex items-center gap-2" data-id="element-1510">
                <span className="text-xs font-mono text-text-primary" data-id="element-1511">
                  {formatNumber(liveSpread, priceDecimals)}
                </span>
                {liveSpreadPercent !== undefined && <span className="text-xs text-text-secondary" data-id="element-1512">
                    ({liveSpreadPercent.toFixed(2)}%)
                  </span>}
              </div>
            </div>}

          {/* Bids (Buy orders) */}
          <div className="flex-1 overflow-y-auto" data-id="element-1513">
            {groupedBids.map((bid, idx) => <OrderRow key={`bid-${idx}`} entry={bid} type="bid" data-id="element-1514" />)}
          </div>
        </>}

      {activeView === 'trades' && <>
          {/* Column Headers */}
          <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[10px] text-text-tertiary uppercase tracking-wider border-b border-border-subtle font-medium" data-id="element-1515">
            <div className="text-right" data-id="element-1516">Price</div>
            <div className="text-right" data-id="element-1517">Quantity</div>
            <div className="text-right" data-id="element-1518">Total</div>
          </div>

          {/* Market Trades */}
          <div className="overflow-y-auto" data-id="element-1519">
            {trades.length > 0 ? trades.map((trade, idx) => {
          const tradeTime = new Date(trade.time);
          const timeString = tradeTime.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
          return <div key={`${trade.time}-${idx}`} className="grid grid-cols-3 gap-2 px-3 py-1 text-xs font-mono hover:bg-background-elevated transition-colors" data-id="element-1520">
                    <div className={`text-right ${trade.side === 'sell' ? 'text-[#E85D75]' : 'text-success'}`} data-id="element-1521">
                      {trade.price.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
              })}
                    </div>
                    <div className="text-right text-text-primary" data-id="element-1522">
                      {trade.volume.toFixed(8)}
                    </div>
                    <div className="text-right text-text-secondary" data-id="element-1523">
                      {timeString}
                    </div>
                  </div>;
        }) : <div className="flex items-center justify-center py-8 text-text-tertiary text-sm" data-id="element-1524">
                Loading trades...
              </div>}
          </div>
        </>}

      {activeView === 'summary' && <div className="overflow-y-auto p-4 space-y-4" data-id="element-1525">
          {/* Time Period Cards */}
          <div className="grid grid-cols-2 gap-3" data-id="element-1526">
            {/* 24H Card */}
            <div className="p-4 bg-background-secondary rounded-lg border border-border-subtle" data-id="element-1527">
              <div className="flex items-center justify-between mb-3" data-id="element-1528">
                <span className="text-sm font-semibold" data-id="element-1529">24H</span>
                <span className="text-sm font-mono text-[#E85D75]" data-id="element-1530">
                  -2.110%
                </span>
              </div>
              <div className="relative h-12 mb-3" data-id="element-1531">
                <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none" data-id="element-1532">
                  <path d="M 0,20 L 30,15 L 60,25 L 100,35" fill="none" stroke="#E85D75" strokeWidth="2" data-id="element-1533" />
                  <circle cx="100" cy="35" r="3" fill="#E85D75" data-id="element-1534" />
                </svg>
              </div>
              <div className="flex items-center justify-between text-xs font-mono" data-id="element-1535">
                <span className="text-text-primary" data-id="element-1536">95,153.1</span>
                <span className="text-text-secondary" data-id="element-1537">97,899.7</span>
              </div>
            </div>

            {/* 1W Card */}
            <div className="p-4 bg-background-secondary rounded-lg border border-border-subtle" data-id="element-1538">
              <div className="flex items-center justify-between mb-3" data-id="element-1539">
                <span className="text-sm font-semibold" data-id="element-1540">1W</span>
                <span className="text-sm font-mono text-success" data-id="element-1541">+4.907%</span>
              </div>
              <div className="relative h-12 mb-3" data-id="element-1542">
                <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none" data-id="element-1543">
                  <path d="M 0,35 L 30,30 L 60,20 L 100,15" fill="none" stroke="#00C87F" strokeWidth="2" data-id="element-1544" />
                  <circle cx="100" cy="15" r="3" fill="#00C87F" data-id="element-1545" />
                </svg>
              </div>
              <div className="flex items-center justify-between text-xs font-mono" data-id="element-1546">
                <span className="text-text-primary" data-id="element-1547">89,603.4</span>
                <span className="text-text-secondary" data-id="element-1548">97,899.7</span>
              </div>
            </div>

            {/* 1M Card */}
            <div className="p-4 bg-background-secondary rounded-lg border border-border-subtle" data-id="element-1549">
              <div className="flex items-center justify-between mb-3" data-id="element-1550">
                <span className="text-sm font-semibold" data-id="element-1551">1M</span>
                <span className="text-sm font-mono text-success" data-id="element-1552">+8.498%</span>
              </div>
              <div className="relative h-12 mb-3" data-id="element-1553">
                <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none" data-id="element-1554">
                  <path d="M 0,35 L 30,28 L 60,22 L 100,12" fill="none" stroke="#00C87F" strokeWidth="2" data-id="element-1555" />
                  <circle cx="100" cy="12" r="3" fill="#00C87F" data-id="element-1556" />
                </svg>
              </div>
              <div className="flex items-center justify-between text-xs font-mono" data-id="element-1557">
                <span className="text-text-primary" data-id="element-1558">84,421.4</span>
                <span className="text-text-secondary" data-id="element-1559">97,899.7</span>
              </div>
            </div>

            {/* 1Y Card */}
            <div className="p-4 bg-background-secondary rounded-lg border border-border-subtle" data-id="element-1560">
              <div className="flex items-center justify-between mb-3" data-id="element-1561">
                <span className="text-sm font-semibold" data-id="element-1562">1Y</span>
                <span className="text-sm font-mono text-[#E85D75]" data-id="element-1563">
                  -5.166%
                </span>
              </div>
              <div className="relative h-12 mb-3" data-id="element-1564">
                <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none" data-id="element-1565">
                  <path d="M 0,15 L 30,20 L 60,28 L 100,35" fill="none" stroke="#E85D75" strokeWidth="2" data-id="element-1566" />
                  <circle cx="100" cy="35" r="3" fill="#E85D75" data-id="element-1567" />
                </svg>
              </div>
              <div className="flex items-center justify-between text-xs font-mono" data-id="element-1568">
                <span className="text-text-primary" data-id="element-1569">74,409.1</span>
                <span className="text-text-secondary" data-id="element-1570">126,198.1</span>
              </div>
            </div>
          </div>

          {/* 24H Volume */}
          <div className="flex items-center justify-between py-2" data-id="element-1571">
            <span className="text-sm text-text-secondary" data-id="element-1572">24H volume</span>
            <span className="text-sm font-mono text-text-primary" data-id="element-1573">
              204,394,428.9 USD
            </span>
          </div>

          {/* Global Stats */}
          <div data-id="element-1574">
            <h4 className="text-xs text-text-tertiary uppercase tracking-wide mb-3" data-id="element-1575">
              Global Stats
            </h4>
            <div className="space-y-2" data-id="element-1576">
              <div className="flex items-center justify-between py-2" data-id="element-1577">
                <span className="text-sm text-text-secondary" data-id="element-1578">
                  Bitcoin rank
                </span>
                <span className="text-sm font-mono text-text-primary" data-id="element-1579">#1</span>
              </div>
              <div className="flex items-center justify-between py-2" data-id="element-1580">
                <span className="text-sm text-text-secondary" data-id="element-1581">Market cap</span>
                <span className="text-sm font-mono text-text-primary" data-id="element-1582">
                  1,906,031,940,431.0 USD
                </span>
              </div>
              <div className="flex items-center justify-between py-2" data-id="element-1583">
                <span className="text-sm text-text-secondary" data-id="element-1584">
                  Global volume
                </span>
                <span className="text-sm font-mono text-text-primary" data-id="element-1585">
                  63,332,819,438.0 USD
                </span>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}